import { useState, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { localizeApiDetail } from '../../i18n/apiError'
import { TrendingUp, Building2, Zap, Clock, Users, ChevronDown, ChevronUp, ChevronRight, ArrowUpDown } from 'lucide-react'
import { leadsAPI } from './api'
import { formatNorwegianDate } from '../../utils/dateUtils'
import Pagination from './Pagination'
import type { NewsResponse, NewsCategoryOrg, NewsNewOrg, NewsGrowthOrg, NewsDrillDownFilter } from './api'
import { useBookmarks } from './useBookmarks'
import BookmarkStar from './BookmarkStar'
import { useLatest } from '../Bruktbil/useLatest'
import LeadsTableExport from './LeadsTableExport'
import { LEADS_EXPORT_LIMIT } from './eksportTilgang'
import type { ColumnDefinition } from '../../utils/exportToCsv'

interface NyheterTabProps {
  /** #438 AC2: same filter bar as Bedrifter/Leads. NACE/mine-NACE only reach
   *  the grids and the category drill-down — the top-level counts come from
   *  a precomputed aggregate with no NACE dimension. */
  markedsomradeIds?: string
  naceFilter?: string
  mineNaceKoder?: boolean
  /** The applied filters, one line each, for the top of the PDF export (#885). */
  exportFilters?: string[]
}

const PERIODS = [
  { value: 'day', labelKey: 'nyheter.periods.day' },
  { value: 'week', labelKey: 'nyheter.periods.week' },
  { value: 'month', labelKey: 'nyheter.periods.month' },
] as const

const SUB_TABS = [
  { value: 'leads', labelKey: 'common.leads' },
  { value: 'orgs', labelKey: 'nyheter.subTabs.orgs' },
  { value: 'vekst', labelKey: 'nyheter.subTabs.vekst' },
] as const
type SubTab = (typeof SUB_TABS)[number]['value']

const PAGE_SIZE = 20

export default function NyheterTab({ markedsomradeIds, naceFilter, mineNaceKoder, exportFilters = [] }: NyheterTabProps) {
  const { t } = useTranslation('leads')
  // Sub-tab lives in the URL (like the main tab) so the back link from a
  // bedrift page restores the view after this component unmounts.
  const [searchParams, setSearchParams] = useSearchParams()
  const ntParam = searchParams.get('nt')
  const subTab: SubTab = SUB_TABS.some(t => t.value === ntParam) ? (ntParam as SubTab) : 'leads'

  const [period, setPeriod] = useState('week')
  const [data, setData] = useState<NewsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  function setSubTab(tab: SubTab) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (tab === 'leads') next.delete('nt')
      else next.set('nt', tab)
      return next
    }, { replace: true })
  }

  // useLatest here and in the grids below: a superseded answer must not
  // replace a newer one (#901).
  useLatest(alive => {
    setLoading(true)
    setError(null)
    leadsAPI.getNews(period, { markedsomrade_ids: markedsomradeIds })
      .then(res => { if (alive()) setData(res.data) })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [period, markedsomradeIds])

  const drillDownFilter = {
    markedsomrade_ids: markedsomradeIds,
    nace_kode: naceFilter || undefined,
    mine_nace_koder: mineNaceKoder,
  }

  const periodLabelKey = PERIODS.find(p => p.value === period)?.labelKey ?? 'nyheter.periods.week'
  const filterLines = [`${t('tableExport.period')}: ${t(periodLabelKey)}`, ...exportFilters]

  if (error) return <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>

  const newLeadsTotal = data?.new_leads.reduce((s, l) => s + l.count, 0) ?? 0

  return (
    <div className="space-y-4">
      {/* Period selector */}
      <div className="flex gap-2">
        {PERIODS.map(p => (
          <button key={p.value} onClick={() => setPeriod(p.value)}
            className={period === p.value ? 'btn-primary' : 'btn-secondary'}>
            {t(p.labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      ) : data && (
        <>
          {/* KPI strip — cards with a matching sub-tab are navigational */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={<Zap className="w-4 h-4" />} label={t('nyheter.kpi.newLeads')}
              value={newLeadsTotal} onClick={() => setSubTab('leads')} />
            <KpiCard icon={<Building2 className="w-4 h-4" />} label={t('nyheter.subTabs.orgs')}
              value={data.new_orgs.count} onClick={() => setSubTab('orgs')} />
            <KpiCard icon={<TrendingUp className="w-4 h-4" />} label={t('nyheter.kpi.vehiclesIn')}
              value={data.vehicles_in} color="green" />
            <KpiCard icon={<Users className="w-4 h-4" />} label={t('nyheter.subTabs.vekst')}
              value={data.employee_growth.count} color="green" onClick={() => setSubTab('vekst')} />
          </div>

          {/* Sub-tab strip */}
          <div className="flex gap-1 border-b border-gray-200 dark:border-dark-border">
            {SUB_TABS.map(st => (
              <button key={st.value} onClick={() => setSubTab(st.value)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  subTab === st.value
                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}>
                {t(st.labelKey)}
              </button>
            ))}
          </div>

          {subTab === 'leads' && <LeadsSubTab data={data} period={period} filter={drillDownFilter} filterLines={filterLines} />}
          {subTab === 'orgs' && <NewOrgsGrid period={period} filter={drillDownFilter} filterLines={filterLines} />}
          {subTab === 'vekst' && <GrowthGrid period={period} filter={drillDownFilter} filterLines={filterLines} />}
        </>
      )}
    </div>
  )
}

/** Nye leads per kategori (with org drill-down) + utgåtte leads. */
function LeadsSubTab({ data, period, filter, filterLines }: { data: NewsResponse; period: string; filter: NewsDrillDownFilter; filterLines: string[] }) {
  const { t } = useTranslation('leads')
  const [expanded, setExpanded] = useState<number | null>(null)

  const categoryColumns: ColumnDefinition[] = [
    { key: 'category_code', label: t('tableExport.code') },
    { key: 'category_name', label: t('common.kategori') },
    { key: 'count', label: t('tableExport.count') },
  ]

  const empty = data.new_leads.length === 0 && data.expired_leads.length === 0

  if (empty) {
    return (
      <div className="card p-6 text-center text-gray-500 dark:text-gray-400">
        {t('nyheter.noChanges')}
      </div>
    )
  }

  return (
    <>
      {data.new_leads.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">{t('nyheter.newLeadsPerCategory')}</h3>
          <LeadsTableExport rows={data.new_leads} columns={categoryColumns}
            title={t('nyheter.newLeadsPerCategory')} filters={filterLines} testId="nyheter-new-leads-export" />
          <div className="space-y-2">
            {data.new_leads.map(l => (
              <div key={l.category_code}>
                <button
                  onClick={() => setExpanded(expanded === l.category_id ? null : l.category_id)}
                  className="w-full flex justify-between items-center rounded-sm px-2 py-1 -mx-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <span className="text-sm flex items-center">
                    {expanded === l.category_id
                      ? <ChevronDown className="w-4 h-4 mr-1 text-gray-400" />
                      : <ChevronRight className="w-4 h-4 mr-1 text-gray-400" />}
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 mr-2">
                      {l.category_code}
                    </span>
                    {l.category_name}
                  </span>
                  <span className="font-bold">{l.count.toLocaleString('nb-NO')}</span>
                </button>
                {expanded === l.category_id && (
                  <CategoryOrgsSubgrid categoryId={l.category_id} categoryName={l.category_name} period={period} filter={filter} filterLines={filterLines} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.expired_leads.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
            <Clock className="w-5 h-5 inline mr-2" />{t('nyheter.expired')}
          </h3>
          <LeadsTableExport rows={data.expired_leads} columns={categoryColumns}
            title={t('nyheter.expired')} filters={filterLines} testId="nyheter-expired-export" />
          <div className="space-y-2">
            {data.expired_leads.map(l => (
              <div key={l.category_code} className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">{l.category_name}</span>
                <span className="font-bold text-gray-500">{l.count.toLocaleString('nb-NO')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

/** Orgs behind one category's new-lead count. The count above comes from the
 *  nightly LeadNewsDaily snapshot while this list is live, so small drift
 *  between the two is expected (see FRONTEND_LEADS_NEWS_GUIDE.md). */
function CategoryOrgsSubgrid({ categoryId, categoryName, period, filter, filterLines }: {
  categoryId: number; categoryName: string; period: string; filter: NewsDrillDownFilter; filterLines: string[]
}) {
  const { t } = useTranslation('leads')
  const navigate = useNavigate()
  const location = useLocation()
  const { isBookmarked, toggle: toggleBookmark } = useBookmarks()
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<NewsCategoryOrg[]>([])
  const [total, setTotal] = useState(0)
  const [totalIsCapped, setTotalIsCapped] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setPage(1) }, [categoryId, period, filter.markedsomrade_ids, filter.nace_kode, filter.mine_nace_koder])

  useLatest(alive => {
    setLoading(true)
    setError(null)
    leadsAPI.getNewsCategoryOrgs(categoryId, period, PAGE_SIZE, (page - 1) * PAGE_SIZE, filter)
      .then(res => {
        if (!alive()) return
        setItems(res.data.items)
        setTotal(res.data.total)
        setTotalIsCapped(res.data.total_is_capped)
        setHasMore(res.data.has_more)
      })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [categoryId, period, page, filter.markedsomrade_ids, filter.nace_kode, filter.mine_nace_koder])

  if (error) return <div className="ml-6 my-2 text-sm text-red-600 dark:text-red-400">{error}</div>
  if (loading) return <div className="ml-6 my-2 animate-pulse h-16 bg-gray-100 dark:bg-dark-border rounded-sm" />
  if (items.length === 0) {
    return <p className="ml-6 my-2 text-sm text-gray-500 dark:text-gray-400">{t('nyheter.noOrgsInPeriod')}</p>
  }

  const columns: ColumnDefinition[] = [
    { key: 'navn', label: t('common.bedrift'), format: (v, row) => v ?? row.org_nr },
    { key: 'org_nr', label: t('common.orgNr') },
    { key: 'poststed', label: t('common.poststed'), format: v => v ?? '—' },
    { key: 'lead_count', label: t('common.leads') },
    { key: 'count_of_owned_cars', label: t('nyheter.eideBiler') },
    { key: 'count_of_leased_cars', label: t('nyheter.leasedeBiler') },
  ]

  return (
    <div className="ml-6 my-2">
      <LeadsTableExport
        rows={items}
        columns={columns}
        title={`${t('nyheter.newLeadsPerCategory')} – ${categoryName}`}
        filters={filterLines}
        loadData={() => leadsAPI.getNewsCategoryOrgs(categoryId, period, LEADS_EXPORT_LIMIT, 0, filter).then(res => res.data.items)}
        capped={totalIsCapped}
        testId="nyheter-category-export"
      />
      <div className="overflow-x-auto">
        <table className="data-table data-table-compact w-full">
          <thead>
            <tr>
              <th className="w-8" aria-label={t('common.mineLeads')} />
              <th className="text-left whitespace-nowrap">{t('common.bedrift')}</th>
              <th className="text-left whitespace-nowrap">{t('common.poststed')}</th>
              <th className="text-right whitespace-nowrap">{t('common.leads')}</th>
              <th className="text-right whitespace-nowrap">{t('nyheter.eideBiler')}</th>
              <th className="text-right whitespace-nowrap">{t('nyheter.leasedeBiler')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map(o => (
              <tr key={o.org_nr}
                onClick={() => navigate(`/leads/bedrift/${o.org_nr}`, { state: { from: location.pathname + location.search } })}
                className="cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border transition-colors">
                <td className="text-center">
                  <BookmarkStar active={isBookmarked(o.org_nr)} onToggle={() => toggleBookmark(o.org_nr)} />
                </td>
                <td className="font-medium">
                  <div className="max-w-[220px] truncate" title={o.navn ?? ''}>{o.navn ?? o.org_nr}</div>
                </td>
                <td className="text-sm">{o.poststed ?? '—'}</td>
                <td className="text-right font-bold">{o.lead_count.toLocaleString('nb-NO')}</td>
                <td className="text-right">{o.count_of_owned_cars.toLocaleString('nb-NO')}</td>
                <td className="text-right">{o.count_of_leased_cars.toLocaleString('nb-NO')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} onPageChange={setPage} total={total}
        totalIsCapped={totalIsCapped} hasMore={hasMore} pageSize={PAGE_SIZE} />
    </div>
  )
}

type SortDir = 'asc' | 'desc'

/** Toggle rule (#438 AC3): clicking the active column flips direction;
 *  clicking a different column switches to it, defaulting to desc. */
function nextSort(current: string | null, currentDir: SortDir, column: string): [string, SortDir] {
  if (current === column) return [column, currentDir === 'asc' ? 'desc' : 'asc']
  return [column, 'desc']
}

function SortableTh({ label, column, sortBy, sortDir, onSort, align = 'left' }: {
  label: string; column: string; sortBy: string | null; sortDir: SortDir
  onSort: (column: string) => void; align?: 'left' | 'right'
}) {
  const active = sortBy === column
  return (
    <th
      className={`whitespace-nowrap cursor-pointer select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(column)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {active
          ? (sortDir === 'asc'
            ? <ChevronUp className="w-3 h-3 text-primary-600 dark:text-primary-400" />
            : <ChevronDown className="w-3 h-3 text-primary-600 dark:text-primary-400" />)
          : <ArrowUpDown className="w-3 h-3 text-gray-300 dark:text-gray-600" />}
      </span>
    </th>
  )
}

/** Paginated grid of organizations registered within the period. */
function NewOrgsGrid({ period, filter, filterLines }: { period: string; filter: NewsDrillDownFilter; filterLines: string[] }) {
  const { t } = useTranslation('leads')
  const navigate = useNavigate()
  const location = useLocation()
  const { isBookmarked, toggle: toggleBookmark } = useBookmarks()
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [items, setItems] = useState<NewsNewOrg[]>([])
  const [total, setTotal] = useState(0)
  const [totalIsCapped, setTotalIsCapped] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setPage(1) }, [period, filter.markedsomrade_ids, filter.nace_kode, filter.mine_nace_koder])

  function handleSort(column: string) {
    const [nextBy, nextDir] = nextSort(sortBy, sortDir, column)
    setSortBy(nextBy)
    setSortDir(nextDir)
    setPage(1)
  }

  useLatest(alive => {
    setLoading(true)
    setError(null)
    leadsAPI.getNewsOrgs<NewsNewOrg>('new_org', period, PAGE_SIZE, (page - 1) * PAGE_SIZE, {
      ...filter, sort_by: sortBy ?? undefined, sort_dir: sortDir,
    })
      .then(res => {
        if (!alive()) return
        setItems(res.data.items)
        setTotal(res.data.total)
        setTotalIsCapped(res.data.total_is_capped)
        setHasMore(res.data.has_more)
      })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [period, page, sortBy, sortDir, filter.markedsomrade_ids, filter.nace_kode, filter.mine_nace_koder])

  if (error) return <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>

  return (
    <div className="card p-6">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
        <Building2 className="w-5 h-5 inline mr-2" />{t('nyheter.subTabs.orgs')}
      </h3>
      {!loading && (
        <LeadsTableExport
          rows={items}
          columns={newOrgColumns(t)}
          title={t('nyheter.subTabs.orgs')}
          filters={filterLines}
          loadData={() => leadsAPI.getNewsOrgs<NewsNewOrg>('new_org', period, LEADS_EXPORT_LIMIT, 0, {
            ...filter, sort_by: sortBy ?? undefined, sort_dir: sortDir,
          }).then(res => res.data.items)}
          capped={totalIsCapped}
          testId="nyheter-new-orgs-export"
        />
      )}
      {loading ? (
        <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      ) : items.length === 0 ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('nyheter.noNewOrgs')}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact w-full">
              <thead>
                <tr>
                  <th className="w-8" aria-label={t('common.mineLeads')} />
                  <th className="text-left whitespace-nowrap">{t('common.bedrift')}</th>
                  <SortableTh label={t('common.nace')} column="nace" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-left whitespace-nowrap max-w-[160px]">{t('common.naceNavn')}</th>
                  <SortableTh label={t('nyheter.ansatte')} column="ansatte" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortableTh label={t('nyheter.postnrSted')} column="postnummer" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh label={t('nyheter.registrert')} column="registrert" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh label={t('nyheter.stiftet')} column="stiftet" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {items.map(o => (
                  <tr key={o.org_nr}
                    onClick={() => navigate(`/leads/bedrift/${o.org_nr}`, { state: { from: location.pathname + location.search } })}
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border transition-colors">
                    <td className="text-center">
                      <BookmarkStar active={isBookmarked(o.org_nr)} onToggle={() => toggleBookmark(o.org_nr)} />
                    </td>
                    <td className="font-medium">
                      <div className="max-w-[220px] truncate" title={o.navn ?? ''}>{o.navn ?? o.org_nr}</div>
                    </td>
                    <td className="font-mono text-xs">{o.nace_kode ?? '—'}</td>
                    <td className="text-xs text-gray-500 max-w-[160px] truncate" title={o.nace_beskrivelse ?? ''}>
                      {o.nace_beskrivelse ?? '—'}
                    </td>
                    <td className="text-right">{o.antall_ansatte?.toLocaleString('nb-NO') ?? '—'}</td>
                    <td className="text-sm">{[o.postnummer, o.poststed].filter(Boolean).join(' ') || '—'}</td>
                    <td className="text-sm whitespace-nowrap">{o.registrert ? formatNorwegianDate(o.registrert) : '—'}</td>
                    <td className="text-sm whitespace-nowrap">{o.stiftet ? formatNorwegianDate(o.stiftet) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} onPageChange={setPage} total={total}
            totalIsCapped={totalIsCapped} hasMore={hasMore} pageSize={PAGE_SIZE} />
        </>
      )}
    </div>
  )
}

/** Paginated grid of employee-growth events within the period. */
function GrowthGrid({ period, filter, filterLines }: { period: string; filter: NewsDrillDownFilter; filterLines: string[] }) {
  const { t } = useTranslation('leads')
  const navigate = useNavigate()
  const location = useLocation()
  const { isBookmarked, toggle: toggleBookmark } = useBookmarks()
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [items, setItems] = useState<NewsGrowthOrg[]>([])
  const [total, setTotal] = useState(0)
  const [totalIsCapped, setTotalIsCapped] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setPage(1) }, [period, filter.markedsomrade_ids, filter.nace_kode, filter.mine_nace_koder])

  function handleSort(column: string) {
    const [nextBy, nextDir] = nextSort(sortBy, sortDir, column)
    setSortBy(nextBy)
    setSortDir(nextDir)
    setPage(1)
  }

  useLatest(alive => {
    setLoading(true)
    setError(null)
    leadsAPI.getNewsOrgs<NewsGrowthOrg>('employee_growth', period, PAGE_SIZE, (page - 1) * PAGE_SIZE, {
      ...filter, sort_by: sortBy ?? undefined, sort_dir: sortDir,
    })
      .then(res => {
        if (!alive()) return
        setItems(res.data.items)
        setTotal(res.data.total)
        setTotalIsCapped(res.data.total_is_capped)
        setHasMore(res.data.has_more)
      })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [period, page, sortBy, sortDir, filter.markedsomrade_ids, filter.nace_kode, filter.mine_nace_koder])

  if (error) return <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>

  return (
    <div className="card p-6">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
        <Users className="w-5 h-5 inline mr-2" />{t('nyheter.subTabs.vekst')}
      </h3>
      {!loading && (
        <LeadsTableExport
          rows={items}
          columns={growthColumns(t)}
          title={t('nyheter.subTabs.vekst')}
          filters={filterLines}
          loadData={() => leadsAPI.getNewsOrgs<NewsGrowthOrg>('employee_growth', period, LEADS_EXPORT_LIMIT, 0, {
            ...filter, sort_by: sortBy ?? undefined, sort_dir: sortDir,
          }).then(res => res.data.items)}
          capped={totalIsCapped}
          testId="nyheter-growth-export"
        />
      )}
      {loading ? (
        <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      ) : items.length === 0 ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('nyheter.noGrowth')}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact w-full">
              <thead>
                <tr>
                  <th className="w-8" aria-label={t('common.mineLeads')} />
                  <th className="text-left whitespace-nowrap">{t('common.bedrift')}</th>
                  <SortableTh label={t('common.poststed')} column="postnummer" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-right whitespace-nowrap">{t('nyheter.ansatteForNa')}</th>
                  <SortableTh label={t('nyheter.endring')} column="pct_change" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortableTh label={t('nyheter.oppdaget')} column="oppdaget" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {items.map(o => (
                  <tr key={o.org_nr}
                    onClick={() => navigate(`/leads/bedrift/${o.org_nr}`, { state: { from: location.pathname + location.search } })}
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border transition-colors">
                    <td className="text-center">
                      <BookmarkStar active={isBookmarked(o.org_nr)} onToggle={() => toggleBookmark(o.org_nr)} />
                    </td>
                    <td className="font-medium">
                      <div className="max-w-[220px] truncate" title={o.navn ?? ''}>{o.navn ?? o.org_nr}</div>
                    </td>
                    <td className="text-sm">{o.poststed ?? '—'}</td>
                    <td className="text-right text-sm text-gray-500 dark:text-gray-400">
                      {o.old_value ?? '—'} &rarr; {o.new_value ?? '—'}
                    </td>
                    <td className="text-right">
                      {o.pct_change !== null ? (
                        <span className="font-bold text-green-600 dark:text-green-400">
                          +{o.pct_change.toLocaleString('nb-NO', { maximumFractionDigits: 1 })}&nbsp;%
                        </span>
                      ) : '—'}
                    </td>
                    <td className="text-sm whitespace-nowrap" title={t('nyheter.oppdagetTitle')}>
                      {o.oppdaget ? formatNorwegianDate(o.oppdaget) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} onPageChange={setPage} total={total}
            totalIsCapped={totalIsCapped} hasMore={hasMore} pageSize={PAGE_SIZE} />
        </>
      )}
    </div>
  )
}

const exportDate = (v: string | null) => (v ? formatNorwegianDate(v) : '—')

function newOrgColumns(t: TFunction<'leads'>): ColumnDefinition[] {
  return [
    { key: 'navn', label: t('common.bedrift'), format: (v, row) => v ?? row.org_nr },
    { key: 'org_nr', label: t('common.orgNr') },
    { key: 'nace_kode', label: t('common.nace'), format: v => v ?? '—' },
    { key: 'nace_beskrivelse', label: t('common.naceNavn'), format: v => v ?? '—' },
    { key: 'antall_ansatte', label: t('nyheter.ansatte'), format: v => (v == null ? '—' : String(v)) },
    { key: 'postnummer', label: t('nyheter.postnrSted'), format: (_v, row) => [row.postnummer, row.poststed].filter(Boolean).join(' ') || '—' },
    { key: 'registrert', label: t('nyheter.registrert'), format: exportDate },
    { key: 'stiftet', label: t('nyheter.stiftet'), format: exportDate },
  ]
}

function growthColumns(t: TFunction<'leads'>): ColumnDefinition[] {
  return [
    { key: 'navn', label: t('common.bedrift'), format: (v, row) => v ?? row.org_nr },
    { key: 'org_nr', label: t('common.orgNr') },
    { key: 'poststed', label: t('common.poststed'), format: v => v ?? '—' },
    { key: 'old_value', label: t('tableExport.ansatteFor'), format: v => (v == null ? '—' : String(v)) },
    { key: 'new_value', label: t('tableExport.ansatteNa'), format: v => (v == null ? '—' : String(v)) },
    { key: 'pct_change', label: t('tableExport.endringPct'), format: v => (v == null ? '—' : Number(v).toLocaleString('nb-NO', { maximumFractionDigits: 1 })) },
    { key: 'oppdaget', label: t('nyheter.oppdaget'), format: exportDate },
  ]
}

function KpiCard({ icon, label, value, color, onClick }: {
  icon: React.ReactNode; label: string; value: number | undefined; color?: string; onClick?: () => void
}) {
  const colorClass = color === 'green' ? 'text-green-600 dark:text-green-400'
    : 'text-primary-600 dark:text-primary-400'
  const content = (
    <>
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
        {icon}
        {label}
      </div>
      {/* ?? 0 guards against a backend/frontend version skew rendering undefined */}
      <div className={`text-2xl font-bold ${colorClass}`}>{(value ?? 0).toLocaleString('nb-NO')}</div>
    </>
  )
  if (onClick) {
    return (
      <button onClick={onClick}
        className="card p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
        {content}
      </button>
    )
  }
  return <div className="card p-4">{content}</div>
}
